import { useDispatch, useSelector } from 'react-redux';
import {
  selectClient,
  selectSession,
  selectSessionAdvisor,
  putMultipleFundingSourceForClientByClientIdRequest,
} from '@/model';
import { SessionType } from '@shared';
import { isTraditionalLTCQuote } from '@/util/quoteTypeGuards';
import { createPolicyFundingSourceFromQuote } from '@/util/policyQuoteCalculations';
import {
  createTraditionalLTCFundingSource,
  type TraditionalLTCQuoteWithROI,
  type QuoteWithROI,
} from '@/features/shop-insurance';

interface UseAddPolicyToLtcPlanningProps {
  clientPartner?: Client | null;
}

export function useAddPolicyToLtcPlanning({
  clientPartner = null,
}: UseAddPolicyToLtcPlanningProps = {}) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const addPolicy = (quote: QuoteWithROI | TraditionalLTCQuoteWithROI) => {
    if (!client?.clientId) {
      console.warn('Cannot add policy: client not found');
      return;
    }

    if (isTraditionalLTCQuote(quote)) {
      const traditionalLTCPolicy = createTraditionalLTCFundingSource(
        quote,
        advisor?.advisorId,
        isAdvisorSession,
      );

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: traditionalLTCPolicy,
          fundingSourceId: null,
        }),
      );
    } else {
      const lifeQuote = quote as QuoteWithROI;

      const finalPolicy = createPolicyFundingSourceFromQuote(
        lifeQuote,
        client,
        isAdvisorSession,
        advisor?.advisorId,
        lifeQuote,
        null,
      );

      dispatch(
        putMultipleFundingSourceForClientByClientIdRequest({
          clientId: client.clientId,
          fundingSourceType: 'ltcPolicy',
          fundingSourceData: finalPolicy,
          fundingSourceId: null,
        }),
      );

      if (lifeQuote.policyType === 'Joint Life' && clientPartner) {
        const partnerFinalPolicy = createPolicyFundingSourceFromQuote(
          lifeQuote,
          clientPartner,
          isAdvisorSession,
          advisor?.advisorId,
          lifeQuote,
          null,
        );

        dispatch(
          putMultipleFundingSourceForClientByClientIdRequest({
            clientId: clientPartner.clientId,
            fundingSourceType: 'ltcPolicy',
            fundingSourceData: partnerFinalPolicy,
            fundingSourceId: null,
          }),
        );
      }
    }
  };

  return { addPolicy };
}
